import { useState, useEffect } from 'react'
import { 
  Container,
  VStack, 
  Heading, 
  Spinner, 
  Alert, 
  AlertIcon, 
  useToast,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Text,
  Box,
  HStack,
  Icon,
  Wrap,
  WrapItem,
  Tooltip
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { FiGlobe, FiMapPin, FiCheck, FiX, FiUsers } from 'react-icons/fi'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import DataTable from '../../common/DataTable/DataTable'
import apiClient from '../../../utils/apiClient'

const Sites = () => {
  const [sites, setSites] = useState([])
  const [filteredSites, setFilteredSites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const toast = useToast()
  const navigate = useNavigate()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0]

  // Enhanced columns with updated payload structure
  const columns = [
    { 
      key: 'name', 
      label: 'Site Name',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <HStack>
            <Icon as={FiGlobe} color="blue.500" />
            <Text fontWeight="medium">{value}</Text>
          </HStack>
          <Text fontSize="xs" color="gray.500">
            {row.domain || 'No domain set'}
          </Text>
        </VStack>
      )
    },
    { 
      key: 'site_status', 
      label: 'Status',
      render: (value) => (
        <Badge 
          colorScheme={
            value === 'active' ? 'green' : 
            value === 'inactive' ? 'red' : 
            value === 'maintenance' ? 'orange' : 'gray'
          }
          variant="subtle"
        >
          {value?.toUpperCase()}
        </Badge>
      )
    },
    { 
      key: 'locales', 
      label: 'Languages',
      render: (value, row) => (
        <VStack align="start" spacing={2}>
          <HStack>
            <Icon as={FiMapPin} color="purple.500" size="sm" />
            <Text fontSize="sm" fontWeight="medium">
              {row.activeLocalesCount || 0} of {row.assignedLocales?.length || 0} Active
            </Text>
          </HStack>
          <Wrap spacing={1}>
            {row.assignedLocales?.slice(0, 4).map(locale => (
              <WrapItem key={locale.locale_id}>
                <Tooltip 
                  label={`${locale.locale_name} (${locale.locale_native_name}) ${locale.is_default ? '- Default' : ''} ${locale.is_active ? '- Active' : '- Inactive'}`}
                >
                  <Badge 
                    size="sm"
                    colorScheme={
                      locale.is_default ? 'blue' : 
                      locale.is_active ? 'green' : 'gray'
                    }
                    variant={locale.is_default ? 'solid' : 'subtle'}
                  >
                    {locale.locale_code}
                    {locale.is_default && ' ★'}
                    {!locale.is_active && ' ●'}
                  </Badge>
                </Tooltip>
              </WrapItem>
            ))}
            {row.assignedLocales?.length > 4 && (
              <WrapItem>
                <Badge size="sm" variant="outline">
                  +{row.assignedLocales.length - 4}
                </Badge>
              </WrapItem>
            )}
          </Wrap>
          {(!row.assignedLocales || row.assignedLocales.length === 0) && (
            <Text fontSize="xs" color="red.500">
              No locales assigned
            </Text>
          )}
        </VStack>
      )
    },
    {
      key: 'default_locale',
      label: 'Default Language',
      render: (value, row) => (
        row.defaultLocale ? (
          <VStack align="start" spacing={1}>
            <HStack>
              <Icon as={FiCheck} color="green.500" size="sm" />
              <Text fontSize="sm" fontWeight="medium">
                {row.defaultLocale.locale_name}
              </Text>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              {row.defaultLocale.locale_native_name}
            </Text>
            <Badge size="sm" colorScheme="blue" variant="solid">
              {row.defaultLocale.locale_code}
            </Badge>
          </VStack>
        ) : (
          <VStack align="start" spacing={1}>
            <HStack>
              <Icon as={FiX} color="red.500" size="sm" />
              <Text fontSize="sm" color="red.500" fontWeight="medium">
                No default set
              </Text>
            </HStack>
            <Text fontSize="xs" color="red.400">
              Required for proper fallback
            </Text>
          </VStack>
        )
      )
    },
    {
      key: 'tenant_info',
      label: 'Details',
      render: (value, row) => (
        <VStack align="start" spacing={1}>
          <HStack>
            <Icon as={FiUsers} color="gray.500" size="sm" />
            <Text fontSize="xs" color="gray.500">
              ID: {row.id}
            </Text>
          </HStack>
          <Text fontSize="xs" color="gray.500">
            Tenant: {row.tenant_id}
          </Text>
          {row.default_locale_id && (
            <Text fontSize="xs" color="gray.500">
              Default Locale ID: {row.default_locale_id}
            </Text>
          )}
        </VStack>
      )
    }
  ]

  const fetchSites = async () => {
    if (!tenantId) {
      setError('No tenant ID found. Please log in again.')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await apiClient(`/sites/${tenantId}`)
      console.log('Fetched sites with locales:', response)

      // Handle both array and object responses
      const sitesData = Array.isArray(response) ? response : response.data || []
      
      // Process the sites data to ensure consistency
      const processedSites = sitesData.map(site => ({
        ...site,
        assignedLocales: site.assignedLocales || [],
        activeLocalesCount: site.activeLocalesCount || site.assignedLocales?.filter(l => l.is_active).length || 0,
        defaultLocale: site.defaultLocale || null
      }))

      setSites(processedSites)
      setFilteredSites(processedSites)
      setError(null)
    } catch (err) {
      console.error('Error fetching sites:', err)
      setError(err.message || 'Failed to load sites')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSites()
  }, [tenantId])

  const handleSearch = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredSites(sites)
      return
    }

    const lowercaseSearch = searchTerm.toLowerCase()
    const filtered = sites.filter(site => {
      // Search in basic site info
      if (site.name?.toLowerCase().includes(lowercaseSearch) ||
          site.domain?.toLowerCase().includes(lowercaseSearch) ||
          site.site_status?.toLowerCase().includes(lowercaseSearch)) {
        return true
      }

      // Search in locale information
      if (site.assignedLocales?.some(locale => 
        locale.locale_name?.toLowerCase().includes(lowercaseSearch) ||
        locale.locale_code?.toLowerCase().includes(lowercaseSearch) ||
        locale.locale_native_name?.toLowerCase().includes(lowercaseSearch)
      )) {
        return true
      }

      // Search in default locale
      if (site.defaultLocale && (
        site.defaultLocale.locale_name?.toLowerCase().includes(lowercaseSearch) ||
        site.defaultLocale.locale_code?.toLowerCase().includes(lowercaseSearch) ||
        site.defaultLocale.locale_native_name?.toLowerCase().includes(lowercaseSearch)
      )) {
        return true
      }

      return false
    })
    
    setFilteredSites(filtered)
  }

  const handleEdit = (site) => {
    navigate(`/sites/edit/${site.id}`)
  }

  const handleCreate = () => {
    navigate(`/sites/create`)
  }

  const handleDelete = async (site) => {
    if (!window.confirm(`Are you sure you want to delete "${site.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      await apiClient(`/sites/${tenantId}/${site.id}`, {
        method: 'DELETE'
      })

      toast({
        title: 'Success',
        description: `Site "${site.name}" deleted successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })

      // Refresh the sites list
      fetchSites()
    } catch (error) {
      console.error('Error deleting site:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete site',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  // Calculate enhanced statistics based on new payload structure
  const getSiteStats = () => {
    const total = sites.length
    const active = sites.filter(site => site.site_status === 'active').length
    const inactive = sites.filter(site => site.site_status === 'inactive').length
    const maintenance = sites.filter(site => site.site_status === 'maintenance').length
    const withLocales = sites.filter(site => site.assignedLocales?.length > 0).length
    const multiLingual = sites.filter(site => site.assignedLocales?.length > 1).length
    const withoutDefault = sites.filter(site => !site.defaultLocale).length
    const fullyConfigured = sites.filter(site => 
      site.defaultLocale && 
      site.assignedLocales?.length > 0 && 
      site.assignedLocales.some(l => l.is_active)
    ).length
    
    return { 
      total, 
      active, 
      inactive, 
      maintenance, 
      withLocales, 
      multiLingual, 
      withoutDefault, 
      fullyConfigured 
    }
  }

  const stats = getSiteStats()

  if (loading) {
    return (
      <Container maxW="container.2xl" py={6}>
        <VStack align="center" justify="center" height="400px">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4} color="gray.500">Loading sites...</Text>
          <Text fontSize="sm" color="gray.400">
            Fetching site configurations and locale assignments
          </Text>
        </VStack>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxW="container.2xl" py={6}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <VStack align="start" spacing={2}>
            <Text fontWeight="medium">Failed to load sites</Text>
            <Text fontSize="sm">{error}</Text>
          </VStack>
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxW="container.2xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Sites</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <Heading size="lg" mb={2}>Sites Management</Heading>
          <Text color="gray.500">
            Manage your sites and their multilingual configurations. Configure default languages and locale assignments.
          </Text>
        </Box>

        {/* Enhanced Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} spacing={4}>
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Total Sites</StatLabel>
                <StatNumber color="blue.500">{stats.total}</StatNumber>
                <StatHelpText>All configured sites</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Active Sites</StatLabel>
                <StatNumber color="green.500">{stats.active}</StatNumber>
                <StatHelpText>Currently active</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>With Locales</StatLabel>
                <StatNumber color="purple.500">{stats.withLocales}</StatNumber>
                <StatHelpText>Have language support</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Multilingual</StatLabel>
                <StatNumber color="orange.500">{stats.multiLingual}</StatNumber>
                <StatHelpText>Multiple languages</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>No Default</StatLabel>
                <StatNumber color="red.500">{stats.withoutDefault}</StatNumber>
                <StatHelpText>Missing default locale</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <Stat>
                <StatLabel>Fully Configured</StatLabel>
                <StatNumber color="teal.500">{stats.fullyConfigured}</StatNumber>
                <StatHelpText>Ready for content</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Enhanced Warnings */}
        {stats.withoutDefault > 0 && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={1}>
              <Text fontWeight="medium">
                {stats.withoutDefault} site{stats.withoutDefault > 1 ? 's' : ''} without default language
              </Text>
              <Text fontSize="sm">
                Sites without a default locale may not function properly. Please assign default languages to ensure proper fallback behavior.
              </Text>
            </VStack>
          </Alert>
        )}

        {stats.total > 0 && stats.withLocales === 0 && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={1}>
              <Text fontWeight="medium">
                No sites have locale assignments
              </Text>
              <Text fontSize="sm">
                Configure language support for your sites to enable multilingual content management.
              </Text>
            </VStack>
          </Alert>
        )}

        {/* Enhanced DataTable */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody p={0}>
            <DataTable
              columns={columns}
              data={filteredSites}
              onSearch={handleSearch}
              onEdit={handleEdit}
              onCreate={handleCreate}
              onDelete={handleDelete}
              searchPlaceholder="Search sites by name, domain, status, or languages..."
              isLoading={loading}
              title="Sites Management"
              createButtonText="Create New Site"
              emptyStateTitle="No sites found"
              emptyStateDescription={
                filteredSites.length === 0 && sites.length > 0
                  ? "No sites match your search criteria. Try adjusting your search terms."
                  : "Get started by creating your first site to manage your content across different languages."
              }
              loadingText="Loading sites and locale configurations..."
            />
          </CardBody>
        </Card>
      </VStack>
    </Container>
  )
}

export default Sites
